import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { ReplaySubject, Subject } from 'rxjs';
import { FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { DashboardService } from '../../../services/dashboard.service';
import { MakeModelFilterData } from '../../../../_shared/models/make-model.model';
import { MakeAndModelFilterComponent } from '../../../../_shared/components/filters/make-model-filter/make-model-filter.component';
import { getStockStatusColor } from '../../../helpers/parts-graph.helper';
import { DateInterval } from '../../../../../_shared/enums';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { AccountFilterComponent } from '../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { PartsGraphRequest, PartsInventoryCategory } from '../../../models/dashboard.model';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-parts-inventory-modal',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    NgbDropdownModule,
    EmptyStateComponent,
    AccountFilterComponent,
    DateFilterComponent,
    MakeAndModelFilterComponent
  ],
  templateUrl: './parts-inventory-modal.component.html',
  styleUrl: './parts-inventory-modal.component.scss'
})
export class PartsInventoryModalComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  private activeModal = inject(NgbActiveModal);
  private filterService = inject(FilterService);
  private dashboardService = inject(DashboardService);
  private errorHandlerService = inject(ErrorHandlerService);

  isLoading: boolean = true;
  title: string = 'sidebar.asset.partsAndInventory';
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  private cancelSubject: Subject<void> = new Subject<void>();
  private modalFilter: WidgetFilterData = { ...this.filterService.filter };

  // Timeline data
  categories: PartsInventoryCategory[] = [];
  xAxisLabels: string[] = [];

  // Additional filters
  makeModelFilter: MakeModelFilterData = { makeModels: [] };
  accountIdsChanged$: ReplaySubject<number[]> = new ReplaySubject(1);
  private makeModelAccountIds: string | null = null;

  displayLabel(name: string): string {
    const keys: Record<string, string> = {
      'Good': 'maintenanceDashboard.inspection.good',
      'Near Standard': 'maintenanceDashboard.inspection.nearStandard',
      'Bad': 'maintenanceDashboard.inspection.bad',
      'Roadworthy': 'maintenanceDashboard.inspection.roadworthy',
      'In Stock': 'maintenanceDashboard.partsInventory.inStock',
      'Low Stock': 'parts.widgets.lowStock',
      'Out of Stock': 'parts.widgets.outOfStock'
    };
    return keys[name] ? this.transloco.translate(keys[name]) : name;
  }

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
  }

  async ngOnInit(): Promise<void> {
    this.xAxisLabels = this.generateXAxisLabels();
    this.syncMakeModelAccounts();
    await this.loadPartsInventoryData();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
    this.accountIdsChanged$.complete();
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.modalFilter = {
        ...this.modalFilter,
        ...this.dateFilter,
        ...this.accountFilter
      };

      this.syncMakeModelAccounts();
      await this.loadPartsInventoryData();
    }
  }

  onMakeModelFilterChange(filter: MakeModelFilterData): void {
    this.makeModelFilter = filter;
    this.loadPartsInventoryData();
  }

  private syncMakeModelAccounts(): void {
    const accountIds = this.modalFilter.accountIds ?? [];
    const accountKey = [...accountIds].sort().join(',');
    if (accountKey === this.makeModelAccountIds) return;

    this.makeModelAccountIds = accountKey;
    this.makeModelFilter = { makeModels: [] };
    this.accountIdsChanged$.next(accountIds);
  }

  async loadPartsInventoryData(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const selectedMakeModel = this.makeModelFilter.makeModels?.[0];

      const request: PartsGraphRequest = {
        accountIds: this.modalFilter.accountIds,
        timeFrom: this.modalFilter.startDate!,
        timeTo: this.modalFilter.endDate!,
        interval: this.modalFilter.interval,
        makeId: selectedMakeModel?.makeId,
        modelId: selectedMakeModel?.modelId
      };

      const response = await this.dashboardService.getPartsStockStatusGraph(request, this.cancelSubject);
      const data = response.data ?? [];
      const total = data.reduce((sum, item) => sum + (Number(item.totalValue) || 0), 0);

      this.categories = total > 0
        ? data.map((item, index) => ({
          name: item.label,
          color: getStockStatusColor(item.label, index),
          endPercent: ((Number(item.totalValue) || 0) / total) * 100
        }))
        : [];

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.categories = [];
        }
      });
    }
  }

  private generateXAxisLabels(): string[] {
    const labels: string[] = [];

    // Generate percentage labels from 0% to 100% in increments of 5%
    for (let i = 0; i <= 100; i += 5) {
      labels.push(`${i}%`);
    }

    return labels;
  }

  getXAxisLabelPosition(index: number): number {
    if (this.xAxisLabels.length <= 1) return 0;
    return (index / (this.xAxisLabels.length - 1)) * 100;
  }

  hasData(): boolean {
    return this.categories && this.categories.length > 0;
  }

  onExport(): void {
    // TODO: Implement export functionality
    console.log('Export clicked');
  }

  close(): void {
    this.activeModal.close();
  }
}